import RestClient from '../../../../api/axiosInterceptor';
import { toast } from 'react-toastify';

/**
 * Displays a toast notification based on the given intent and message.
 *
 * @param {object} toastService - The toast service used to display notifications.
 * @param {string} intent - The intent of the toast ('success', 'error', 'info', 'warning').
 * @param {string} message - The message to display in the toast notification.
 * @throws {Error} Throws an error if an invalid intent is provided or there is an issue displaying the toast.
 */
export const showToast = (toastService, intent, message) => {
  try {
    // Ensure the intent is a valid type
    if (typeof intent !== 'string' || !message) {
      throw new Error(
        'Invalid input: intent must be a string and message is required.'
      );
    }

    // Determine which type of toast to show based on the intent
    switch (intent.toLowerCase()) {
      case 'success':
        toastService.success(message);
        break;
      case 'error':
        toastService.error(message);
        break;
      case 'info':
        toastService.info(message);
        break;
      case 'warning':
        toastService.warning(message);
        break;
      default:
        // If no specific intent is matched, show a neutral/default toast notification
        toastService.default(message);
    }
  } catch (error) {
    console.error('Error occurred while showing toast: ', error.message);
  }
};

// Adapter for react-toastify
export const ReactToastifyAdapter = {
  success: (message) => toast.success(message),
  error: (message) => toast.error(message),
  info: (message) => toast.info(message),
  warning: (message) => toast.warning(message),
  default: (message) => toast(message)
};

/**
 * Verifies the reCAPTCHA token with the backend.
 *
 * @param {string} captchaValue - The reCAPTCHA token to verify.
 * @param {function} showToast - The function to display toast notifications.
 * @returns {Promise<boolean>} - Returns a promise that resolves to a boolean indicating the verification result.
 */
export const verifyRecaptcha = async (captchaValue) => {
  try {
    const formData = new URLSearchParams();
    formData.append('captchaValue', captchaValue);

    const response = await RestClient.post(
      '/pharmacy/verify-captcha/',
      formData, // Send form data instead of JSON
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }
    );

    if (response.data.success) {
      showToast(
        toast,
        'success',
        response.data.message || 'reCAPTCHA verified successfully.'
      );
      return true;
    } else {
      showToast(
        toast,
        'error',
        response.data.message || 'Invalid reCAPTCHA. Please try again.'
      );
      return false;
    }
  } catch (error) {
    console.error('Error verifying reCAPTCHA:', error.message);
    showToast(toast, 'error', 'An error occurred while verifying reCAPTCHA.');
    return false;
  }
};

export const validateConfirmPassword = (
  password,
  confirmPassword,
  setErrors
) => {
  if (confirmPassword !== password) {
    setErrors((prevErrors) => ({
      ...prevErrors,
      confirmPassword: 'Passwords must match!'
    }));
  } else {
    setErrors((prevErrors) => ({ ...prevErrors, confirmPassword: '' }));
  }
};

/**
 * Checks the completion status of a user's profile using Basic Authentication
 *
 * @async
 * @param {string} credentials - User's email address and password
 * @returns {Promise<Object>} Profile completion status
 * @property {boolean} isComplete - Whether the profile is complete
 * @property {Array<string>} incompleteFields - List of fields that need to be completed
 * @property {string|null} error - Error message if request fails, null otherwise
 */
export const checkProfileCompletionStatus = async (credentials) => {
  try {
    // Make request with Basic Auth header
    const response = await RestClient.get('/auth/profile/check-completion/', {
      headers: {
        Authorization: `Basic ${credentials}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 200) {
      return {
        isComplete: true,
        incompleteFields: [],
        error: null
      };
    }

    return {
      isComplete: false,
      incompleteFields: response.data?.incomplete_fields || [],
      error: null
    };
  } catch (error) {
    const errorMessage =
			error.response?.data?.message || 'Failed to check profile completion';
    console.error('Profile completion check error:', error);
    return {
      isComplete: false,
      incompleteFields: [],
      error: errorMessage
    };
  }
};

/**
 * Converts a string to title case format, handling special cases
 * @param {string} str - The string to convert
 * @returns {string} The converted string in title case
 */
export const toTitleCase = (str) => {
  if (!str) return '';

  // Handle hyphenated words and multiple spaces
  return str
    .toLowerCase()
    .split(/[\s-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
